/* eslint-disable camelcase */
import checkStatus from '../session-status';
import {
	LoadMeeting,
	UpdateUserMeetingSettings,
} from '../../connection/meeting';
import HandleInvitation from '../handle-invitation';
import HandleSelectedMeeting from '../handle-selected-meeting';
import {
	PresenterLogin,
	ProducerLogin,
	UpdateProfile,
	ForgotPasswordEvents,
	UpdatePasswordEvents,
	GetCurrentProfile,
	CreateAccount,
	VerifyAccount,
	ForgotPasswordGreenroom,
	UpdatePasswordGreenroom,
	TempPhone,
	ConfirmCode,
	UpdateChannel,
	CreatePlatformAccount,
} from '../../connection/auth';
import { CreatePlatformAccountResponse } from '../types/connection';
import { ConnectedUser } from '../../stores/types/connection';
import {
	ShowroomsTokens,
	Meeting,
	ShowSession,
	TutorialVideoState,
} from '../types/session';
import { RefreshShowroomsTokens } from '../../connection/showrooms';
import {
	AddShowSession,
	GetShowSessionsByMeetingId,
	GetShowSessionTypes,
	EditShowSession,
	DeleteShowSession,
} from '../../connection/show-sessions';
import { Channel } from '../types/home';

interface Action {
	type: string;
	payload: any;
}

export const SET_PATH = 'SET_PATH';
export function setPath(path: string): Action {
	return {
		type: SET_PATH,
		payload: path,
	};
}

interface AsyncAction {
	type: string;
	promise: Promise<any>
}

interface AsyncActionGeneric<T> extends AsyncAction {
	promise: Promise<T>
}

export const SET_PAGE_STATUS = 'SET_PAGE_STATUS';
export function setPageStatus(): AsyncAction {
	return {
		type: SET_PAGE_STATUS,
		promise: checkStatus(),
	};
}

export const SET_SESSION = 'SET_SESSION';
export function setSession(session: string): Action {
	return {
		type: SET_SESSION,
		payload: session,
	};
}

export const SIGN_IN_HOST = 'SIGN_IN_HOST';
export function signInHost(
	email: string,
	password: string,
	app: string,
	storedTwoFactorEvents?: string,
): AsyncAction {
	return {
		type: SIGN_IN_HOST,
		promise: ProducerLogin(email, password, app, storedTwoFactorEvents),
	};
}

export const SIGN_IN_PRESENTER = 'SIGN_IN_PRESENTER';
export function signInPresenter(email: string, password: string): AsyncAction {
	return {
		type: SIGN_IN_PRESENTER,
		promise: PresenterLogin(email, password),
	};
}

export const SIGN_IN_LIMITED_DEVICE = 'SIGN_IN_LIMITED_DEVICE';
export function signInLimitedDevice(token:string): Action {
	return {
		type: SIGN_IN_LIMITED_DEVICE,
		payload: token,
	};
}

export const LOAD_MEETING = 'LOAD_MEETING';
export function loadMeeting(id: string, token: string): AsyncAction {
	return {
		type: LOAD_MEETING,
		promise: LoadMeeting(id, token),
	};
}

export const CLEAR_MEETING = 'CLEAR_MEETING';
export function clearMeeting(): Action {
	return {
		type: CLEAR_MEETING,
		payload: undefined,
	};
}

export const SET_SELECTED_MEETING = 'SET_SELECTED_MEETING';
export function setSelectedMeeting(meeting: any): Action {
	return {
		type: SET_SELECTED_MEETING,
		payload: meeting,
	};
}

export const SET_ONBOARDING_ROUTE = 'SET_ONBOARDING_ROUTE';
export function setOnboardingRoute(route: number): Action {
	return {
		type: SET_ONBOARDING_ROUTE,
		payload: route,
	};
}

export const CHECK_INVITATION = 'CHECK_INVITATION';
export function checkInvitation(token: string, invitation: string): AsyncAction {
	return {
		type: CHECK_INVITATION,
		promise: HandleInvitation(token, invitation),
	};
}

export const CHECK_SELECTED_MEETING = 'CHECK_SELECTED_MEETING';
export function checkSelectedMeeting(token: string, meeting: number): AsyncAction {
	return {
		type: CHECK_SELECTED_MEETING,
		promise: HandleSelectedMeeting(token, meeting),
	};
}

export const SET_ONBOARDING_COMPLETE = 'SET_ONBOARDING_COMPLETE';
export function setOnboardingComplete(isComplete: boolean): Action {
	return {
		type: SET_ONBOARDING_COMPLETE,
		payload: isComplete,
	};
}

export const SET_ONBOARDING_TAB = 'SET_ONBOARDING_TAB';
export function setOnboardingTab(tab: number): Action {
	return {
		type: SET_ONBOARDING_TAB,
		payload: tab,
	};
}

export const SET_PROFILE_INFO = 'SET_PROFILE_INFO';
export function setProfileInfo(options: any): Action {
	return {
		type: SET_PROFILE_INFO,
		payload: options,
	};
}

export const UPDATE_PROFILE = 'UPDATE_PROFILE';
export function updateProfile(
	gr_user: number,
	columns: any,
	token: string,
): AsyncAction {
	return {
		type: UPDATE_PROFILE,
		promise: UpdateProfile(gr_user, columns, token),
	};
}

export const GET_PROFILE = 'GET_PROFILE';
export function getProfile(
	token: string,
	app_signed_in_with: string,
	showrooms_access_token?: string,
): AsyncAction {
	return {
		type: GET_PROFILE,
		promise: GetCurrentProfile(token, app_signed_in_with, showrooms_access_token),
	};
}

export const CREATE_ACCOUNT = 'CREATE_ACCOUNT';
export function createAccount(
	first_name: string,
	last_name: string,
	password: string,
	email: string,
	is_producer: boolean,
	verification_code: string | undefined,
): AsyncAction {
	return {
		type: CREATE_ACCOUNT,
		promise: CreateAccount(first_name, last_name, password, email, is_producer, verification_code),
	};
}

export const VERIFY_ACCOUNT = 'VERIFY_ACCOUNT';
export function verifyAccount(code: string): AsyncAction {
	return {
		type: VERIFY_ACCOUNT,
		promise: VerifyAccount(code),
	};
}

export const USER_CONNECTED = 'USER_CONNECTED';
export function userConnected(user: ConnectedUser) {
	return {
		type: USER_CONNECTED,
		payload: user,
	};
}

export const USER_UPDATED = 'USER_UPDATED';
export function userUpdated(user: ConnectedUser) {
	return {
		type: USER_UPDATED,
		payload: user,
	};
}

export const USER_DISCONNECTED = 'USER_DISCONNECTED';
export function userDisconnected(user: ConnectedUser) {
	return {
		type: USER_DISCONNECTED,
		payload: user,
	};
}

export const USERS_LIST = 'USERS_LIST';
export function usersList(users: ConnectedUser[], resetConnectedUsers: boolean) {
	return {
		type: USERS_LIST,
		payload: { users, resetConnectedUsers },
	};
}

export const SET_WEBRTC_ID = 'SET_WEBRTC_ID';
export function setWebrtcId(id: string | number) {
	return {
		type: SET_WEBRTC_ID,
		payload: id,
	};
}

export const SET_SCREENSHARE_WEBRTC_ID = 'SET_SCREENSHARE_WEBRTC_ID';
export function setScreenShareWebrtcId(id: string | number) {
	return {
		type: SET_SCREENSHARE_WEBRTC_ID,
		payload: id,
	};
}

export const SET_CURRENT_VIEWERS = 'SET_CURRENT_VIEWERS';
export function setCurrentViewers(num: number) {
	return {
		type: SET_CURRENT_VIEWERS,
		payload: num,
	};
}

export const FORGOT_PASSWORD_EVENTS = 'FORGOT_PASSWORD_EVENTS';
export function forgotPasswordEvents(email: string) {
	return {
		type: FORGOT_PASSWORD_EVENTS,
		promise: ForgotPasswordEvents(email),
	};
}

export const FORGOT_PASSWORD_GREENROOM = 'FORGOT_PASSWORD_GREENROOM';
export function forgotPasswordGreenroom(email: string, isProducer: boolean) {
	return {
		type: FORGOT_PASSWORD_GREENROOM,
		promise: ForgotPasswordGreenroom(email, isProducer),
	};
}

export const UPDATE_PASSWORD_EVENTS = 'UPDATE_PASSWORD_EVENTS';
export function updatePasswordEvents(password: string, code: string) {
	return {
		type: UPDATE_PASSWORD_EVENTS,
		promise: UpdatePasswordEvents(password, code),
	};
}

export const UPDATE_PASSWORD_GREENROOM = 'UPDATE_PASSWORD_GREENROOM';
export function updatePasswordGreenroom(password: string, code: string) {
	return {
		type: UPDATE_PASSWORD_EVENTS,
		promise: UpdatePasswordGreenroom(password, code),
	};
}

export const SET_TUTORIAL_VIDEO_STATE = 'SET_TUTORIAL_VIDEO_STATE';
export function setTutorialVideoState(state: TutorialVideoState) {
	return {
		type: SET_TUTORIAL_VIDEO_STATE,
		payload: state,
	};
}

export const SET_WATCHER_ONLY = 'SET_WATCHER_ONLY';
export function setWatcherOnly(isWatcher: boolean) {
	return {
		type: SET_WATCHER_ONLY,
		payload: isWatcher,
	};
}

export const SET_AVATAR_POPOVER_OPEN = 'SET_AVATAR_POPOVER_OPEN';
export function setAvatarPopoverOpen(open: boolean) {
	return {
		type: SET_AVATAR_POPOVER_OPEN,
		payload: open,
	};
}

export const SET_SELECTED_EVENT = 'SET_SELECTED_EVENT';
export function setSelectedEvent(event: any) {
	return {
		type: SET_SELECTED_EVENT,
		payload: event,
	};
}

export const SET_IOS_OPENTOK_SESSION = 'SET_IOS_OPENTOK_SESSION';
export function setIosOpentokSession(session: string) {
	return {
		type: SET_IOS_OPENTOK_SESSION,
		payload: session,
	};
}

export const SET_EXPECT_IOS_STREAM = 'SET_EXPECT_IOS_STREAM';
export function setExpectIosStream(expect: boolean) {
	return {
		type: SET_EXPECT_IOS_STREAM,
		payload: expect,
	};
}

export const SET_PROFILE = 'SET_PROFILE';
export function setProfile(token: string): Action {
	return {
		type: SET_PROFILE,
		payload: token,
	};
}

export const SET_HELP_MENU_OPEN = 'SET_HELP_MENU_OPEN';
export function setHelpMenuOpen(open: boolean): Action {
	return {
		type: SET_HELP_MENU_OPEN,
		payload: open,
	};
}

export const SET_ACTIVE_HELP_SUBMENU = 'SET_ACTIVE_HELP_SUBMENU';
export function setActiveHelpSubmenu(submenu: string): Action {
	return {
		type: SET_ACTIVE_HELP_SUBMENU,
		payload: submenu,
	};
}

export const SET_EMAIL_ENTERED = 'SET_EMAIL_ENTERED';
export function setEmailEntered(email: string) {
	return {
		type: SET_EMAIL_ENTERED,
		payload: email,
	};
}

export const SET_SELECTED_APP = 'SET_SELECTED_APP';
export function setSelectedApp(app: string) {
	return {
		type: SET_SELECTED_APP,
		payload: app,
	};
}

export const SET_APP_SIGNED_IN_WITH = 'SET_APP_SIGNED_IN_WITH';
export function setAppSignedInWith(app: string) {
	return {
		type: SET_APP_SIGNED_IN_WITH,
		payload: app,
	};
}

export const REFRESH_SHOWROOMS_TOKENS = 'REFRESH_SHOWROOMS_TOKENS';
export function refreshShowroomsTokens(tokensJWT: string): AsyncAction {
	return {
		type: REFRESH_SHOWROOMS_TOKENS,
		promise: RefreshShowroomsTokens(tokensJWT),
	};
}

export const SET_SHOWROOMS_TOKENS = 'SET_SHOWROOMS_TOKENS';
export function setShowroomsTokens(tokens: ShowroomsTokens) {
	return {
		type: SET_SHOWROOMS_TOKENS,
		payload: tokens,
	};
}

export const SET_BACKSTAGE_COMMS_KEY_STATE = 'SET_BACKSTAGE_COMMS_KEY_STATE';
export function setBackstageCommsKeyState(setting: number) {
	return {
		type: SET_BACKSTAGE_COMMS_KEY_STATE,
		payload: setting,
	};
}

export const SET_BACKSTAGE_COMMS_TARGET_WEBRTCID = 'SET_BACKSTAGE_COMMS_TARGET_WEBRTCID';
export function setBackstageCommsTargetWebRtcId(webRtcId: string) {
	return {
		type: SET_BACKSTAGE_COMMS_TARGET_WEBRTCID,
		payload: webRtcId,
	};
}

export const SET_HAS_UPDATED_STATE = 'SET_HAS_UPDATED_STATE';
export function setHasUpdatedState(hasUpdatedState: boolean) {
	return {
		type: SET_HAS_UPDATED_STATE,
		payload: hasUpdatedState,
	};
}

export const SET_MEETING_MODAL_OPEN = 'SET_MEETING_MODAL_OPEN';
export function setMeetingModalOpen(open: boolean) {
	return {
		type: SET_MEETING_MODAL_OPEN,
		payload: open,
	};
}

export const SET_SIGN_IN_ERROR = 'SET_SIGN_IN_ERROR';
export function setSignInError(text: string) {
	return {
		type: SET_SIGN_IN_ERROR,
		payload: text,
	};
}

export const SET_MEETING_TO_EDIT = 'SET_MEETING_TO_EDIT';
export function setMeetingToEdit(meeting: Meeting | null) {
	return {
		type: SET_MEETING_TO_EDIT,
		payload: meeting,
	};
}

export const GET_SHOW_SESSION_TYPES = 'GET_SHOW_SESSION_TYPES';
export function getShowSessionTypes(token: string): AsyncAction {
	return {
		type: GET_SHOW_SESSION_TYPES,
		promise: GetShowSessionTypes(token),
	};
}

export const GET_SHOW_SESSIONS = 'GET_SHOW_SESSIONS';
export function getShowSessions(token: string, meetingId: number): AsyncAction {
	return {
		type: GET_SHOW_SESSIONS,
		promise: GetShowSessionsByMeetingId(token, meetingId),
	};
}

export const ADD_SHOW_SESSION = 'ADD_SHOW_SESSION';
export function addShowSession(token: string, showSession: ShowSession): AsyncAction {
	return {
		type: ADD_SHOW_SESSION,
		promise: AddShowSession(token, showSession),
	};
}

export const EDIT_SHOW_SESSION = 'EDIT_SHOW_SESSION';
export function editShowSession(token: string, showSession: ShowSession): AsyncAction {
	return {
		type: EDIT_SHOW_SESSION,
		promise: EditShowSession(token, showSession),
	};
}

export const DELETE_SHOW_SESSION = 'DELETE_SHOW_SESSION';
export function deleteShowSession(token: string, showSession: ShowSession): AsyncAction {
	return {
		type: DELETE_SHOW_SESSION,
		promise: DeleteShowSession(token, showSession),
	};
}

export const SET_RESTRICTED_USER_STATE = 'SET_RESTRICTED_USER_STATE';
export function setRestrictedUserState(restrictedUserState: number) {
	return {
		type: SET_RESTRICTED_USER_STATE,
		payload: restrictedUserState,
	};
}

export const SET_TEMP_NUMBER = 'SET_TEMP_NUMBER';
export function setTempNumber(number: string, temp_profile: string): AsyncAction {
	return {
		type: SET_TEMP_NUMBER,
		promise: TempPhone(number, temp_profile),
	};
}

export const CONFIRM_CODE = 'CONFIRM_CODE';
export function confirmCode(
	temp_profile: string,
	code: string,
	request_storage: boolean,
): AsyncAction {
	return {
		type: CONFIRM_CODE,
		promise: ConfirmCode(temp_profile, code, request_storage),
	};
}
export const SET_AWAITING_PRODUCER = 'SET_AWAITING_PRODUCER';
export function setAwaitingProducer(awaitingProducer: boolean) {
	return {
		type: SET_AWAITING_PRODUCER,
		payload: awaitingProducer,
	};
}

export const SET_JUST_VERIFIED = 'SET_JUST_VERIFIED';
export function setJustVerified(justVerified: boolean): Action {
	return {
		type: SET_JUST_VERIFIED,
		payload: justVerified,
	};
}

export const UPDATE_CHANNEL = 'UPDATE_CHANNEL';
export function updateChannel(token: string, channel: Channel): AsyncAction {
	return {
		type: UPDATE_CHANNEL,
		promise: UpdateChannel(token, channel),
	};
}

export const CLEAR_PRODUCER_APPS = 'CLEAR_PRODUCER_APPS';
export function clearProducerApps(): Action {
	return {
		type: CLEAR_PRODUCER_APPS,
		payload: undefined,
	};
}

export const SYNC_SESSION_REDUCER_STATE = 'SYNC_SESSION_REDUCER_STATE';
export function syncSessionReducerState(state: any) {
	return {
		type: SYNC_SESSION_REDUCER_STATE,
		payload: state,
	};
}

export const SET_USER_MEETING_SETTINGS = 'SET_USER_MEETING_SETTINGS';
export function setUserMeetingSettings(userMeetingSettings: Record<string, number[]>) {
	return {
		type: SET_USER_MEETING_SETTINGS,
		payload: userMeetingSettings,
	};
}

export const UPDATE_USER_MEETING_SETTINGS = 'UPDATE_USER_MEETING_SETTINGS';
export function updateUserMeetingSettings(
	token: string,
	meetingId: number,
	userId: number,
	settings: any,
): AsyncAction {
	return {
		type: UPDATE_USER_MEETING_SETTINGS,
		promise: UpdateUserMeetingSettings(token, meetingId, userId, settings),
	};
}

export const SET_ACCOUNT_DELETED = 'SET_ACCOUNT_DELETED';
export function setAccountDeleted(deleted: boolean) {
	return {
		type: SET_ACCOUNT_DELETED,
		payload: deleted,
	};
}

export const SET_WARNING_MODAL_SENT = 'SET_WARNING_MODAL_SENT';
export function setWarningModalSent(sent: boolean) {
	return {
		type: SET_WARNING_MODAL_SENT,
		payload: sent,
	};
}

export const UPDATE_APP_VERSION = 'UPDATE_APP_VERSION';
export function updateAppVersion(version: string) {
	return {
		type: UPDATE_APP_VERSION,
		payload: version,
	};
}

export const CREATE_PLATFORM_ACCOUNT = 'CREATE_PLATFORM_ACCOUNT';
export function createPlatformAccount(
	email: string,
	firstName?: string,
	lastName?: string,
	meetingHash?: string,
	redirect?: string,
): AsyncActionGeneric<CreatePlatformAccountResponse> {
	return {
		type: CREATE_PLATFORM_ACCOUNT,
		promise: CreatePlatformAccount(
			email,
			firstName,
			lastName,
			meetingHash,
			redirect,
		),
	};
}

export const SET_BLP_SESSION = 'SET_BLP_SESSION';
export function setBlpSession(data: {
	accessToken?: string, refreshToken?: string, idToken?: string
}) {
	return {
		type: SET_BLP_SESSION,
		payload: data,
	};
}

export const USER_REMOTE_EDITED = 'USER_REMOTE_EDITED';
export function userRemoteEdited(message: {
	gr_user: number,
	first_name?: string,
	last_name?: string,
	title?: string,
	location?: string
	avatar?: string,
	bg_image?: string,
}) {
	return {
		type: USER_REMOTE_EDITED,
		payload: message,
	};
}

export const SET_COMPANION_MODE = 'SET_COMPANION_MODE';
export function setCompanionMode(isCompanionMode: boolean) {
	return {
		type: SET_COMPANION_MODE,
		payload: isCompanionMode,
	};
}

export const SET_IS_SYNERGYSKY_SESSION = 'SET_IS_SYNERGYSKY_SESSION';
export function setIsSynergyskySession(isSynergyskySession: boolean) {
	return {
		type: SET_IS_SYNERGYSKY_SESSION,
		payload: isSynergyskySession,
	};
}
